import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';

import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { AutocompleteOption } from '../models/autocomplete-option.model';

interface AutocompleteEditorParams {
  placeholder: string;
  values: () => AutocompleteOption[];
  loadingData: () => boolean;
  loadOptions: () => void;
  filterStartsWith?: boolean;
}

@Component({
  selector: 'lib-autocomplete-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AutocompleteComponent],
  templateUrl: './autocomplete-editor.component.html',
  styleUrls: ['./autocomplete-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteEditorComponent implements ICellEditorAngularComp {
  private readonly autocomplete =
    viewChild<AutocompleteComponent>('autocomplete');

  private readonly params = signal<
    (ICellEditorParams & AutocompleteEditorParams) | null
  >(null);

  public readonly control = new FormControl<string>('', {
    nonNullable: true,
  });

  public placeholder = '';
  public filterStartsWith = false;
  public cellWidth = 0;
  public cellHeight = 0;

  public readonly selectOptions = computed<AutocompleteOption[]>(
    () => this.params()?.values() ?? []
  );

  public readonly loader = computed<boolean>(
    () => this.params()?.loadingData() ?? false
  );

  public agInit(params: ICellEditorParams & AutocompleteEditorParams): void {
    this.params.set(params);

    this.placeholder = params.placeholder;
    this.filterStartsWith = params.filterStartsWith ?? false;

    const cell = params.eGridCell;

    this.cellWidth = cell.clientWidth + 4;
    this.cellHeight = cell.clientHeight + 4;

    this.control.setValue(params.value ?? '', {
      emitEvent: false,
    });
  }

  public afterGuiAttached(): void {
    queueMicrotask(() => {
      this.autocomplete()?.focus();
    });
  }

  public getValue(): string {
    const controlValue = this.control.value.trim();

    if (!controlValue) {
      return '';
    }

    const currentValue = controlValue.toLowerCase();

    const matchedOption = this.selectOptions().find((option) => {
      const optionValue = option.value.toLowerCase();
      const displayValue = option.displayValue?.toLowerCase();

      return optionValue === currentValue || displayValue === currentValue;
    });

    return matchedOption?.value ?? '';
  }

  public onOptionSelected(): void {
    this.params()?.api.stopEditing();
  }

  public loadOptions(): void {
    this.params()?.loadOptions();
  }
}
